// src/components/Nav.js
import React, { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import "./Nav.css";
import logo from "../assets/logo.png";

// Cart hook to watch localStorage
function useCart() {
  const [cartCount, setCartCount] = useState(0);

  const updateCartCount = () => {
    try {
      const cart = JSON.parse(localStorage.getItem('checkout_cart') || '[]');
      const count = cart.reduce((sum, item) => sum + (item.quantity || 1), 0);
      setCartCount(count);
    } catch (error) {
      setCartCount(0);
    }
  };

  useEffect(() => {
    updateCartCount();
    
    // Listen for storage changes (from other tabs/pages)
    window.addEventListener('storage', updateCartCount);
    
    // Custom event for same-page updates
    window.addEventListener('cartUpdated', updateCartCount);
    
    // Poll every 2 seconds as fallback
    const interval = setInterval(updateCartCount, 2000);
    
    return () => {
      window.removeEventListener('storage', updateCartCount);
      window.removeEventListener('cartUpdated', updateCartCount);
      clearInterval(interval);
    };
  }, []);

  return cartCount;
}

export default function Nav() {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const { pathname } = useLocation();
  const cartCount = useCart();

  const toggleMobileMenu = () => {
    setMobileMenuOpen(!mobileMenuOpen);
  };

  const closeMobileMenu = () => {
    setMobileMenuOpen(false);
  };

  // Close mobile menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (mobileMenuOpen && !event.target.closest('.site-header')) {
        closeMobileMenu();
      }
    };

    if (mobileMenuOpen) {
      document.addEventListener('click', handleClickOutside);
      // Prevent body scroll when menu is open
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }

    return () => {
      document.removeEventListener('click', handleClickOutside);
      document.body.style.overflow = 'unset';
    };
  }, [mobileMenuOpen]);

  // Close mobile menu on route change
  useEffect(() => {
    closeMobileMenu();
  }, [pathname]);

  return (
    <header className="site-header">
      <div className="header-left">
        <img src={logo} alt="Chefs2Table logo" className="logo" />
        <div className="title-container">
          <h1 className="site-title">Chefs2Table</h1>
          <p className="tagline">Delicious Meals<br />Exceptional Deals</p>
        </div>
      </div>

      {/* Hamburger Menu Button */}
      <button 
        className="hamburger-menu"
        onClick={toggleMobileMenu}
        aria-label="Toggle navigation menu"
        aria-expanded={mobileMenuOpen}
      >
        <span className={`hamburger-line ${mobileMenuOpen ? 'active' : ''}`}></span>
        <span className={`hamburger-line ${mobileMenuOpen ? 'active' : ''}`}></span>
        <span className={`hamburger-line ${mobileMenuOpen ? 'active' : ''}`}></span>
      </button>

      <nav className={`nav-bar ${mobileMenuOpen ? 'open' : ''}`} aria-label="Main navigation">
        <ul className="nav-list">
          <li className={pathname === "/" ? "active" : ""}>
            <Link to="/" onClick={closeMobileMenu}>Home</Link>
          </li>

          {/* Chefs: no dropdown, just link to the page with pill menu */}
          <li className={pathname.startsWith("/chefs") ? "active" : ""}>
            <Link to="/chefs" onClick={closeMobileMenu}>Meet Local Chefs</Link>
          </li>

          {/* Join Our Team - standalone menu item */}
          <li className={pathname === "/join-team" ? "active" : ""}>
            <Link to="/join-team" onClick={closeMobileMenu}>Join Our Team</Link>
          </li>

          <li className={pathname === "/about" ? "active" : ""}>
            <Link to="/about" onClick={closeMobileMenu}>About</Link>
          </li>

          {/* Order Now - primary CTA - moved to the right */}
          <li className={pathname === "/order-now" ? "active order-cta order-cta-right" : "order-cta order-cta-right"}>
            <Link to="/order-now" onClick={closeMobileMenu}>Order Now</Link>
          </li>

          {/* Cart Icon */}
          <li className="cart-icon-wrapper">
            <Link to="/order-now" className="cart-icon-link" onClick={closeMobileMenu}>
              🛒
              {cartCount > 0 && <span className="cart-badge">{cartCount}</span>}
            </Link>
          </li>
        </ul>
      </nav>
    </header>
  );
}